import { useEffect, useRef, useState } from 'react';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { parseDate } from 'lib/date';
import { WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { FieldLabel, FieldValue, RowButton } from './categoryCard/styles';
import { INBOUND_REFERRALS_ROUTES } from './constants';
import { sentToSummary } from './ReferralSentTo';

// The duplicate-review body (OLIO-2729, restyled per Figma node 10317:22949): the search
// surfaced other active referrals that may be this same patient. Each candidate is confirmed or
// dismissed on its own — there's no cross-candidate selection to make first. Confirming cancels
// THIS referral — carrying its requested facilities onto the survivor (OLIO-3194), linked to the
// survivor — through a confirmation modal (a real consequence, so it still gets a second click).
// Dismissing is local-only and not persisted: it just drops that candidate from the list. Once
// every candidate has been dismissed (or there were none to begin with), the step completes
// itself as no_match — the same outcome the old standalone "Not a Duplicate" button used to
// record by hand.

// Value content is camelCased by the API middleware (see eligibilityData.ts).
export interface DuplicateCandidate {
  workflowId: string;
  status?: string;
  createdAt?: string;
  matchStrength: string;
  patientFirstName?: string;
  patientLastName?: string;
  patientDob?: string;
  patientGender?: string;
  // The referral source this candidate RESOLVED to, never its packet's raw wording (OLIO-3382).
  // Absent while the Confirm Referral Source step is still asking.
  referralSource?: string | null;
  // Every Olio building the candidate was sent to — all of them, not just the ones this viewer
  // holds, so the count answers how widely the patient was blasted out. Each carries its group
  // type so the count can name the right kind of building; the type is absent on an untyped one.
  sentToFacilities?: { name?: string; groupType?: { displayName?: string } | null }[];
  // Whether THIS viewer can actually open this referral. Decided per request by the API, not
  // stored with the candidate — the duplicate search is client-wide while reading a run is
  // group-scoped, so the answer differs by who is looking. Absent on payloads served before
  // OLIO-3382, which is why the reader below defaults it to viewable: an older API must not
  // silently strip every link on the card.
  viewable?: boolean;
}

interface Props {
  action: WorkflowAction;
  onComplete: (actionId: string, outputs: Record<string, string>) => unknown;
  isCompleting: boolean;
}

const EMPTY = '—';

const formatDate = (value?: string) => parseDate(value ?? null)?.toLocaleDateString() ?? EMPTY;

// One facility is named; several are counted, because the names don't fit and the number is what
// the coordinator is actually reading for. The count goes through the rail card's own
// `sentToSummary`, which reads the plural off the buildings' group type — referrals go to agencies
// and hospitals as well as SNFs, and hardcoding one here is how the card ends up calling an agency
// a SNF. The API always sends at least one facility (a referral must target a group), so the empty
// fallback only guards an older payload.
const sentTo = (facilities?: DuplicateCandidate['sentToFacilities']) => {
  const list = facilities ?? [];
  if (list.length === 0) return EMPTY;
  return list.length === 1 ? (list[0].name ?? EMPTY) : sentToSummary(list);
};

export default function DuplicatePatientReview({ action, onComplete, isCompleting }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<DuplicateCandidate | null>(null);
  const raw = action.outputFor('duplicate_candidates');
  const candidates = Array.isArray(raw) ? (raw as DuplicateCandidate[]) : [];
  const visible = candidates.filter((candidate) => !dismissed.has(candidate.workflowId));

  // Fires once every candidate the search actually found has been dismissed — never for a
  // malformed/empty payload (candidates.length === 0 from the start), which stays a visible,
  // inert empty state rather than silently resolving corrupt data as "no match". The ref stops a
  // second completion from firing if onComplete's own re-render brings this effect back around.
  const autoCompleted = useRef(false);
  useEffect(() => {
    if (candidates.length === 0 || visible.length > 0 || autoCompleted.current) return;
    autoCompleted.current = true;
    onComplete(action.id, { duplicate_patient_result: 'no_match' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.length]);

  const dismiss = (workflowId: string) => setDismissed((current) => new Set(current).add(workflowId));

  const confirm = () => {
    if (!confirming) return;
    const candidate = confirming;
    setConfirming(null);
    onComplete(action.id, {
      duplicate_patient_result: 'confirmed_match',
      duplicate_of_workflow_id: candidate.workflowId,
    });
  };

  return (
    <Wrapper data-testid='duplicate-patient-review'>
      <IntroText>
        <BodySmallBold>
          {visible.length} possible existing referral{visible.length === 1 ? '' : 's'} found for this patient
        </BodySmallBold>
        <Explanation>
          Review the matches below. Confirm a match to cancel this referral and add its requested facilities to the
          existing one — otherwise dismiss it and keep working this referral.
        </Explanation>
      </IntroText>

      <Cards>
        {/* Every candidate dismissed: the effect above completes this as no_match on its own, but
            its call can fail silently (the page's onComplete wrapper swallows the error) and leave
            the action stuck at action_required with nothing else on screen to act on. This button
            is the manual escape hatch — same completion, user-triggered instead of automatic. */}
        {candidates.length > 0 && visible.length === 0 && (
          <EmptyState data-testid='duplicate-empty-state'>
            <BodySmall>Every possible match has been dismissed.</BodySmall>
            <RowButton
              variant='outline-primary'
              disabled={isCompleting}
              onClick={() => onComplete(action.id, { duplicate_patient_result: 'no_match' })}
              data-testid='duplicate-mark-no-match'>
              Mark as No Match
            </RowButton>
          </EmptyState>
        )}
        {visible.map((candidate) => (
          <CandidateCard key={candidate.workflowId} data-testid='duplicate-candidate'>
            <TopRow>
              {/* A candidate the viewer can't open is named but not linked. The duplicate search
                  looks across the whole client while reading a referral is scoped to the
                  viewer's own buildings, so this card can legitimately surface a referral they
                  have no access to — and it used to link there anyway, sending them to a 404. */}
              <NameCell>
                {candidate.viewable === false ? (
                  <PatientName data-testid='duplicate-candidate-name'>
                    {candidate.patientFirstName} {candidate.patientLastName}
                  </PatientName>
                ) : (
                  <PatientLink
                    to={`${INBOUND_REFERRALS_ROUTES.BASE}/${candidate.workflowId}`}
                    target='_blank'
                    data-testid='duplicate-candidate-link'>
                    {candidate.patientFirstName} {candidate.patientLastName}
                  </PatientLink>
                )}
              </NameCell>
              <CardActions>
                <RowButton
                  variant='outline-gray'
                  disabled={isCompleting}
                  onClick={() => dismiss(candidate.workflowId)}
                  data-testid='duplicate-dismiss'>
                  Dismiss
                </RowButton>
                <RowButton
                  variant='outline-primary'
                  disabled={isCompleting}
                  onClick={() => setConfirming(candidate)}
                  data-testid='duplicate-confirm'>
                  Confirm
                </RowButton>
              </CardActions>
            </TopRow>
            {/* An em dash rather than a dropped field, unlike the header chips: these are aligned
                columns read DOWN a stack of candidates, and omitting one would shuffle the rest
                out of line just when the coordinator is comparing them. The gender field this
                replaces was wrong because it was ALWAYS empty, not because empty can't be shown. */}
            <DetailRow data-testid='duplicate-candidate-details'>
              <div>
                <FieldLabel>DOB</FieldLabel>
                <FieldValue data-testid='duplicate-dob'>{formatDate(candidate.patientDob)}</FieldValue>
              </div>
              <div>
                <FieldLabel>Gender</FieldLabel>
                <FieldValue data-testid='duplicate-gender'>
                  {candidate.patientGender ? startCase(candidate.patientGender) : EMPTY}
                </FieldValue>
              </div>
              <div>
                <FieldLabel>Created On</FieldLabel>
                <FieldValue data-testid='duplicate-created-on'>{formatDate(candidate.createdAt)}</FieldValue>
              </div>
              <div>
                <FieldLabel>Referral Source</FieldLabel>
                <FieldValue data-testid='duplicate-referral-source'>{candidate.referralSource || EMPTY}</FieldValue>
              </div>
              <div>
                <FieldLabel>Referral Sent To</FieldLabel>
                <FieldValue data-testid='duplicate-sent-to'>{sentTo(candidate.sentToFacilities)}</FieldValue>
              </div>
            </DetailRow>
          </CandidateCard>
        ))}
      </Cards>

      {confirming && (
        <StandardModal
          title='Confirm Duplicate'
          onCancel={() => setConfirming(null)}
          colorTheme={ColorThemeName.Primary}>
          <ModalBody>
            <BodySmall>
              Are you sure this referral is a duplicate? This referral will be{' '}
              <BodySmallBold as='span'>canceled</BodySmallBold> and linked to the existing referral you selected.
            </BodySmall>
            <BodySmall>
              Any facilities requested here that the existing referral does not already have will be added to it.
            </BodySmall>
          </ModalBody>
          <ModalButtons>
            <Button variant='ghost' onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button variant='primary' disabled={isCompleting} onClick={confirm} data-testid='duplicate-confirm-modal'>
              Confirm Duplicate
            </Button>
          </ModalButtons>
        </StandardModal>
      )}
    </Wrapper>
  );
}

// The settled note on a completed search that confirmed a duplicate: this run was
// canceled; the link points at the surviving referral.
export function DuplicateLinkNote({ action }: { action: WorkflowAction }) {
  const survivorId = action.outputFor('duplicate_of_workflow_id');
  if (typeof survivorId !== 'string' || !survivorId) return null;

  // Same rule as the candidate cards above: the survivor can be a referral outside this viewer's
  // buildings, and linking there lands them on a 404. `undefined` means the API said nothing (an
  // older payload), which keeps the link rather than removing one that used to work.
  if (action.outputViewable('duplicate_of_workflow_id') === false) {
    return <BodySmall data-testid='duplicate-link-note'>This referral was linked to an existing referral.</BodySmall>;
  }

  return (
    <BodySmall data-testid='duplicate-link-note'>
      <ViewLink to={`${INBOUND_REFERRALS_ROUTES.BASE}/${survivorId}`} data-testid='duplicate-survivor-link'>
        View the existing referral
      </ViewLink>
    </BodySmall>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Explanation = styled(BodySmall)`
  color: ${colors.black50};
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

// Two stacked rows (Figma 14056:9380 and siblings): who/what/act on top, the identifying detail
// underneath. The detail row is what a coordinator scans DOWN across several candidates, so it
// gets the full width rather than sharing a line with the buttons.
const CandidateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

// Wraps rather than squashing: at narrow widths the buttons drop below the name instead of the
// name being clipped.
const TopRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
`;

// `min-width: 0` carried over from the CardMain this row replaced, where it was added deliberately
// (#1422), lost to the #1442 revert, and put back in #1444. A flex item defaults to min-width:auto
// and so refuses to shrink below its content: without this a long unbroken patient name widens the
// whole card rather than wrapping. `overflow-wrap` is what actually breaks such a name, since
// wrapping alone can't split a single long word.
const NameCell = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

// The candidate's name is the link into its referral (replacing the old separate "View referral"
// link) — Figma's bold, underlined blue treatment.
const PatientLink = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.blue100};
  text-decoration: underline;
`;

// The same name, for a referral this viewer can't open: identical size and weight so the cards
// still line up, but ordinary text rather than an affordance that leads nowhere.
const PatientName = styled(BodySmall)`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

// `margin-left: auto` pins the buttons right on a line they share with the name and pill, and
// still behaves once the row wraps.
const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

// The label-above-value treatment the extraction card uses directly above this one
// (ExtractionResults), so the two read as one surface. Values wrap rather than truncate: a grid
// cell can take a second line, where the header's nowrap chips had to be capped.
const DetailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 40px;
`;

const ViewLink = styled(Link)`
  color: ${colors.blue100};
  font-size: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 24px;
`;

const ModalButtons = styled(ButtonGroup)`
  justify-content: flex-end;
`;
